/* eslint-disable jsx-a11y/anchor-is-valid */
import ChevronRightLogo from '@mui/icons-material/ChevronRightRounded';
import ExternalLinkLogo from '@mui/icons-material/OpenInNewRounded';
import { Box, Divider, Link, Typography, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useState } from 'react';
import { ATCOPaletteColor } from '../../../theme/ATCOPaletteColor';
import { TypographyVariants } from '../../../theme/TypographyVariants';
import { Navigation, OverallHeader } from '../Header';
import BodySecondLevelMenu from './BodySecondLevelMenu';
import BodyThirdLevelMenu from './BodyThirdLevelMenu';
import { ButtonType } from '../../../types/enums';
import ATCOButton from '../../../common/ATCOButton';
import useResponsiveHelper from '../../common/hooks/useResponsiveHelper';

interface BodyMainMenuProps {
    navobj?: Navigation[];
    headerobj: OverallHeader;
    menuItem: string;
    onCloseClick: (mobileMenuClicked: boolean) => void;
    isMobileOrTablet: boolean;
}

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        backgroundColor: ATCOPaletteColor.white,
    },
    container: {
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
        [theme.breakpoints.up('desktop')]: {
            paddingBottom: '1.875rem',
            minWidth: '330px',
        },
        [theme.breakpoints.down('desktop')]: {
            paddingBottom: '1.875rem',
            width: '300px',
        },
        [theme.breakpoints.down(912)]: {
            height: '92vh', // 100vh - 8vh (header height)
            justifyContent: 'space-between',
            width: '100%',
        },
        [theme.breakpoints.down('tablet')]: {
            height: '92vh', // 100vh - 8vh (header height)
            justifyContent: 'space-between',
            width: '100%',
        },
    },
    menu: {
        display: 'flex',
        flexDirection: 'column',
        [theme.breakpoints.down(912)]: {
            marginTop: '1.938rem',
            marginRight: '1.375rem',
            marginLeft: '1rem',
        },
    },
    item: {
        display: 'flex',
        marginTop: '0.5rem',
        [theme.breakpoints.up(912)]: {
            height: '44px',
        },
        [theme.breakpoints.down(912)]: {
            marginTop: '1.5rem',
        },
    },
    text: {
        display: 'flex',
        width: '100%',
        justifyContent: 'space-between',
        marginRight: '0.625rem',
        [theme.breakpoints.down(912)]: {
            color: theme.palette.neutral1,
            alignItems: 'center',
        },
        [theme.breakpoints.only('tablet')]: {
            color: theme.palette.neutral1,
            alignItems: 'center',
        },
    },
    store: {
        [theme.breakpoints.down(912)]: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginBottom: '3.5rem',
        },
        [theme.breakpoints.only('tablet')]: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginBottom: '3.5rem',
        },
    },
    close: {
        height: '24px !important',
        width: '24px !important',
    },
    secondLevelLink: {
        display: 'flex',
        width: '100%',
        '&:hover': {
            background: theme.palette.neutral8,
        },
    },
    secondLevelText: {},
}));

const BodyMainMenu: React.FC<BodyMainMenuProps> = (props: BodyMainMenuProps) => {
    const [subMenuClicked, setSubMenuClicked] = useState(false);
    const [secondLevelSubMenuClicked, setSecondLevelSubMenuClicked] = useState(false);
    const [thirdLevelSubMenuClicked, setThirdLevelSubMenuClicked] = useState(false);
    const [subMenuItem, setSubMenuItem] = useState('');
    const [secondLevelSubMenuItem, setSecondLevelSubMenuItem] = useState('');
    const [thirdLevelSubMenuItem, setThirdLevelSubMenuItem] = useState('');
    const [mainMenuItem, setMainMenuItem] = useState('');
    const [mainMenuClosed, setMainMenuClosed] = useState(false);
    const { isDesktop } = useResponsiveHelper();

    const theme = useTheme();
    const classes = useStyles();

    const { navobj, headerobj, menuItem, onCloseClick, isMobileOrTablet } = props;

    const menuNavObject = navobj?.find((e: Navigation) => e.navText === menuItem);

    return (
        <Box className={classes.root}>
            {!isMobileOrTablet ? (
                <Box sx={{ display: 'flex' }}>
                    <Box className={classes.container}>
                        {menuNavObject?.child?.map((secondLevelNavItem: Navigation) => {
                            return (
                                <Box key={secondLevelNavItem.navText} className={classes.item}>
                                    {(secondLevelNavItem.child !== null && !secondLevelNavItem.external) || undefined ? (
                                        <Box className={classes.secondLevelLink} sx={{ background: subMenuClicked && subMenuItem === secondLevelNavItem.navText ? theme.palette.neutral8 : 'inherit' }}>
                                            <Link
                                                aria-label={secondLevelNavItem.altTextPath}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    marginLeft: '2.5rem',
                                                    marginRight: '1rem',
                                                }}
                                                className={classes.text}
                                                onClick={() => {
                                                    setSubMenuClicked(subMenuItem === secondLevelNavItem.navText ? !subMenuClicked : true);
                                                    setSubMenuItem(secondLevelNavItem?.navText);
                                                    setMainMenuItem(menuItem);
                                                }}
                                            >
                                                <Typography variant={isDesktop ? TypographyVariants.bodyBold : TypographyVariants.button}>{secondLevelNavItem.navText}</Typography>
                                                <ChevronRightLogo sx={{ color: theme.palette.primary1 }} />
                                            </Link>
                                        </Box>
                                    ) : (
                                        <Box
                                            display="flex"
                                            sx={{
                                                width: '100%',
                                                '&:hover': {
                                                    background: theme.palette.neutral8,
                                                },
                                            }}
                                        >
                                            <Link
                                                aria-label={secondLevelNavItem.altTextPath}
                                                className={classes.text}
                                                marginLeft="2.5rem"
                                                marginRight="1rem"
                                                alignItems="center"
                                                href={secondLevelNavItem.path}
                                                target={secondLevelNavItem.external ? '_blank' : '_self'}
                                                rel="noopener noreferrer"
                                                onClick={() => {
                                                    setSubMenuClicked(false);
                                                }}
                                                justifyContent={secondLevelNavItem.external ? 'flex-start' : 'space-between'}
                                            >
                                                <Typography variant={isDesktop ? TypographyVariants.bodyBold : TypographyVariants.button}>{secondLevelNavItem.navText}</Typography>
                                                {secondLevelNavItem.external ? (
                                                    <ExternalLinkLogo
                                                        aria-label={secondLevelNavItem.altTextIcon}
                                                        sx={{
                                                            filter: 'brightness(0) saturate(100%) invert(0%) sepia(59%) saturate(1143%) hue-rotate(104deg) brightness(107%) contrast(105%)',
                                                            marginLeft: '10px',
                                                            height: '16px',
                                                        }}
                                                    />
                                                ) : null}
                                            </Link>
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                    <Divider orientation="vertical" flexItem />
                </Box>
            ) : mainMenuClosed ? null : !subMenuClicked ? (
                <Box className={classes.container}>
                    <Box className={classes.menu}>
                        <Box>
                            {navobj?.map((firstLevelNavItem: Navigation) => {
                                return (
                                    <Box key={firstLevelNavItem.navText} className={classes.item}>
                                        {(firstLevelNavItem.child !== null || undefined) && !firstLevelNavItem.external ? (
                                            <Link
                                                aria-label={firstLevelNavItem.altTextPath}
                                                className={classes.text}
                                                onClick={() => {
                                                    setSubMenuClicked(true);
                                                    setSubMenuItem(firstLevelNavItem?.navText);
                                                }}
                                            >
                                                <Box>
                                                    <Typography variant={TypographyVariants.bodyBold}>{firstLevelNavItem.navText}</Typography>
                                                </Box>
                                                <ChevronRightLogo sx={{ color: theme.palette.primary1 }} />
                                            </Link>
                                        ) : firstLevelNavItem.external ? (
                                            <Link aria-label={firstLevelNavItem.altTextPath} href={firstLevelNavItem.path} target="_blank" rel="noopener noreferrer" className={classes.text}>
                                                <Box>
                                                    <Typography variant={TypographyVariants.bodyBold}>{firstLevelNavItem.navText}</Typography>
                                                </Box>
                                                <ExternalLinkLogo
                                                    titleAccess={firstLevelNavItem.altTextIcon}
                                                    sx={{
                                                        height: '16px',
                                                        filter: 'brightness(0) saturate(100%) invert(14%) sepia(6%) saturate(321%) hue-rotate(335deg) brightness(100%) contrast(93%)',
                                                    }}
                                                />
                                            </Link>
                                        ) : (
                                            <Link aria-label={firstLevelNavItem.altTextPath} href={firstLevelNavItem.path} className={classes.text}>
                                                <Typography variant={TypographyVariants.bodyBold}>{firstLevelNavItem.navText}</Typography>
                                            </Link>
                                        )}
                                    </Box>
                                );
                            })}
                            {(headerobj.firstButtonLabel || headerobj.secondButtonLabel) && <Divider flexItem sx={{ mb: 4, mt: 3 }} />}
                            {headerobj.firstButtonLabel && (
                                <Link aria-label={headerobj.firstButtonLabel} href={headerobj.firstButtonUrl} className={classes.text}>
                                    <Box>
                                        <Typography variant={TypographyVariants.bodyBold}>{headerobj.firstButtonLabel}</Typography>
                                    </Box>
                                </Link>
                            )}
                            {headerobj.secondButtonLabel && (
                                <Link aria-label={headerobj.secondButtonLabel} href={headerobj.secondButtonUrl} className={classes.text} sx={{ mt: 3 }}>
                                    <Box>
                                        <Typography variant={TypographyVariants.bodyBold}>{headerobj.secondButtonLabel}</Typography>
                                    </Box>
                                </Link>
                            )}
                        </Box>
                    </Box>
                    <Box className={classes.store}>
                        {headerobj.storeLabel && (
                            <ATCOButton
                                type={ButtonType.PRIMARY_SKY_LIGHT}
                                text={headerobj.storeLabel}
                                sx={{
                                    width: '90vw',
                                    backgroundColor: ATCOPaletteColor.primary1,
                                    color: ATCOPaletteColor.white,
                                }}
                                altText={headerobj?.alternateTextForStoreUrl}
                                url={headerobj.storeUrl}
                            />
                        )}
                    </Box>
                </Box>
            ) : (
                <BodySecondLevelMenu
                    setSubMenuClicked={setSubMenuClicked}
                    setMainMenuClosed={setMainMenuClosed}
                    onCloseClick={onCloseClick}
                    setSecondLevelSubMenuClicked={setSecondLevelSubMenuClicked}
                    setSecondLevelSubMenuItem={setSecondLevelSubMenuItem}
                    setThirdLevelSubMenuClicked={setThirdLevelSubMenuClicked}
                    setThirdLevelSubMenuItem={setThirdLevelSubMenuItem}
                    subMenuItem={subMenuItem}
                    navobj={navobj?.find((e: Navigation) => e.navText === subMenuItem)}
                    secondLevelSubMenuItem={secondLevelSubMenuItem}
                    secondLevelSubMenuClicked={secondLevelSubMenuClicked}
                    thirdLevelSubMenuItem={thirdLevelSubMenuItem}
                    thirdLevelSubMenuClicked={thirdLevelSubMenuClicked}
                />
            )}
            <BodyThirdLevelMenu
                subMenuClicked={subMenuClicked}
                menuItem={menuItem}
                mainMenuItem={mainMenuItem}
                setSubMenuClicked={setSubMenuClicked}
                setSubMenuItem={setSubMenuItem}
                subMenuItem={subMenuItem}
                mainMenuNavobj={menuNavObject}
                subMenuNavObject={menuNavObject?.child?.find((e: Navigation) => e.navText === subMenuItem)}
            />
        </Box>
    );
};

export default BodyMainMenu;
